import type { Language } from '../types';
import { getTranslation } from './translations';

export type FastingUIStatus = 'idle' | 'scheduled' | 'active' | 'completed';

export interface IntermittentFastingSnapshot {
    status: FastingUIStatus;
    goalHours: number;
    startedAt: number | null;
    scheduledStartAt: number | null;
    nowMs: number;
}

export const DEFAULT_FASTING_GOAL_HOURS = 16;

export const FASTING_GOAL_PRESETS = [8, 12, 14, 16, 18, 20] as const;

export function formatDigitalCountdown(ms: number): string {
    const { hours, minutes } = formatDurationFromMs(ms);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
}

export function formatDigitalRemainingLine(ms: number, language: Language | undefined): string {
    return getTranslation(language, 'fastingCountdownRemaining').replace('{time}', formatDigitalCountdown(ms));
}

export function getGoalDurationMs(goalHours: number): number {
    return goalHours * 60 * 60 * 1000;
}

export function getElapsedMs(snapshot: Pick<IntermittentFastingSnapshot, 'startedAt' | 'nowMs'>): number {
    if (snapshot.startedAt == null) return 0;
    return Math.max(0, snapshot.nowMs - snapshot.startedAt);
}

export function getMsUntilScheduledStart(snapshot: Pick<IntermittentFastingSnapshot, 'status' | 'scheduledStartAt' | 'nowMs'>): number {
    if (snapshot.status !== 'scheduled' || snapshot.scheduledStartAt == null) return 0;
    return Math.max(0, snapshot.scheduledStartAt - snapshot.nowMs);
}

export function formatStartTimeLabel(
    startTimestamp: number,
    language: Language | undefined,
    referenceMs: number = Date.now(),
): string {
    const time = formatClockTime(startTimestamp, language);
    if (isTomorrow(referenceMs, startTimestamp)) {
        return getTranslation(language, 'fastingStartsAtTomorrow').replace('{time}', time);
    }
    return getTranslation(language, 'fastingStartsAt').replace('{time}', time);
}

export function formatScheduledCountdownLine(ms: number, language: Language | undefined): string {
    return getTranslation(language, 'fastingStartsIn').replace('{time}', formatDigitalCountdown(ms));
}

export function toDateTimeLocalValue(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function resolveFastingLocale(language?: Language): string {
    if (!language) return 'en-GB';
    if (language === 'he') return 'he-IL';
    if (language === 'ar') return 'ar';
    return language;
}

export function splitDateTimeLocalValue(value: string): { date: string; hour: number; minute: number } | null {
    const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
    if (!match) return null;
    const hour = Number(match[2]);
    const minute = Number(match[3]);
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return { date: match[1], hour, minute };
}

export function joinDateTimeLocalValue(date: string, hour: number, minute: number): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date}T${pad(hour)}:${pad(minute)}`;
}

export function formatDateTimeLocalPreview(value: string, language?: Language): string {
    const parsed = parseDateTimeLocalValue(value);
    if (!parsed) return '';
    return new Intl.DateTimeFormat(resolveFastingLocale(language), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(parsed);
}

export function parseDateTimeLocalValue(value: string): Date | null {
    if (!value.trim()) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isDateTimeLocalInRange(value: string, min?: string, max?: string): boolean {
    const parsed = parseDateTimeLocalValue(value);
    if (!parsed) return false;
    const timestamp = parsed.getTime();
    if (min) {
        const minDate = parseDateTimeLocalValue(min);
        if (minDate && timestamp < minDate.getTime()) return false;
    }
    if (max) {
        const maxDate = parseDateTimeLocalValue(max);
        if (maxDate && timestamp > maxDate.getTime()) return false;
    }
    return true;
}

export function getRemainingMs(snapshot: IntermittentFastingSnapshot): number {
    if (snapshot.status === 'scheduled') {
        return getMsUntilScheduledStart(snapshot);
    }
    if (snapshot.status !== 'active' || snapshot.startedAt == null) return 0;
    return Math.max(0, getGoalDurationMs(snapshot.goalHours) - getElapsedMs(snapshot));
}

export function getProgressRatio(snapshot: IntermittentFastingSnapshot): number {
    if (snapshot.status === 'completed') return 1;
    if (snapshot.status !== 'active' || snapshot.startedAt == null) return 0;
    const goalMs = getGoalDurationMs(snapshot.goalHours);
    if (goalMs <= 0) return 0;
    return Math.min(1, getElapsedMs(snapshot) / goalMs);
}

export function isGoalReached(snapshot: IntermittentFastingSnapshot): boolean {
    if (snapshot.status !== 'active' || snapshot.startedAt == null) {
        return snapshot.status === 'completed';
    }
    return getRemainingMs(snapshot) <= 0;
}

export function getEndTimestamp(snapshot: Pick<IntermittentFastingSnapshot, 'startedAt' | 'goalHours'>): number | null {
    if (snapshot.startedAt == null) return null;
    return snapshot.startedAt + getGoalDurationMs(snapshot.goalHours);
}

export function formatClockTime(timestamp: number, language?: Language): string {
    return new Intl.DateTimeFormat(resolveFastingLocale(language), {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(new Date(timestamp));
}

export function formatDurationParts(totalMinutes: number): { hours: number; minutes: number; seconds: number } {
    const safe = Math.max(0, Math.floor(totalMinutes));
    const hours = Math.floor(safe / 60);
    const minutes = safe % 60;
    const seconds = 0;
    return { hours, minutes, seconds };
}

export function formatDurationFromMs(ms: number): { hours: number; minutes: number; seconds: number } {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { hours, minutes, seconds };
}

export function formatRemainingLabel(
    ms: number,
    language: Language | undefined,
    options?: { includeSeconds?: boolean },
): string {
    const { hours, minutes, seconds } = formatDurationFromMs(ms);
    const h = getTranslation(language, 'fastingHoursShort');
    const m = getTranslation(language, 'fastingMinutesShort');

    if (options?.includeSeconds && hours === 0 && minutes < 60) {
        const s = getTranslation(language, 'fastingSecondsShort');
        if (hours > 0) {
            return `${hours}${h}${String(minutes).padStart(2, '0')}${m}`;
        }
        if (minutes > 0) {
            return `${minutes}${m}${String(seconds).padStart(2, '0')}${s}`;
        }
        return `${seconds}${s}`;
    }

    if (hours > 0) {
        return `${hours}${h}${String(minutes).padStart(2, '0')}${m}`.trim();
    }
    return `${minutes}${m}`.trim();
}

export function formatGoalHoursLabel(hours: number, language: Language | undefined): string {
    return getTranslation(language, 'fastingHoursGoal').replace('{hours}', String(hours));
}

export function formatGoalLine(hours: number, language: Language | undefined): string {
    return getTranslation(language, 'fastingGoalHours').replace('{hours}', String(hours));
}

export function getProjectedEndTimestamp(goalHours: number, fromMs: number = Date.now()): number {
    return fromMs + getGoalDurationMs(goalHours);
}

export function isTomorrow(fromMs: number, targetMs: number): boolean {
    const from = new Date(fromMs);
    const target = new Date(targetMs);
    const nextDay = new Date(from);
    nextDay.setDate(from.getDate() + 1);
    return (
        target.getFullYear() === nextDay.getFullYear()
        && target.getMonth() === nextDay.getMonth()
        && target.getDate() === nextDay.getDate()
    );
}

export function formatEndTimeLabel(
    endTimestamp: number,
    language: Language | undefined,
    referenceMs: number = Date.now(),
): string {
    const time = formatClockTime(endTimestamp, language);
    if (isTomorrow(referenceMs, endTimestamp)) {
        return getTranslation(language, 'fastingEndsAtTomorrow').replace('{time}', time);
    }
    return getTranslation(language, 'fastingEndsAt').replace('{time}', time);
}

export function formatEndsAtLine(endTimestamp: number, language: Language | undefined): string {
    return formatEndTimeLabel(endTimestamp, language);
}

export function formatRemainingLeftLine(ms: number, language: Language | undefined): string {
    return getTranslation(language, 'fastingRemainingLeft').replace('{time}', formatRemainingLabel(ms, language));
}
